/**
 * Local project management.
 *
 * Everything here is client-side. Deletion asks first — it is the only action
 * in the app that destroys work with no undo.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogFooter, EmptyState, FolderOpen, IconButton } from '../ui';
import { Copy, Edit3, Trash } from '../ui/icons';
import type { ProjectRecord, ProjectStore, ProjectSummary } from '../../persistence';
import type { Locale, Translator } from '../../i18n';

export interface ProjectsDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly store: ProjectStore;
  readonly currentProjectId: string;
  readonly onOpen: (record: ProjectRecord) => void;
  readonly onCreate: () => void;
  readonly locale: Locale;
  readonly t: Translator['t'];
}

export function ProjectsDialog({
  open,
  onClose,
  store,
  currentProjectId,
  onOpen,
  onCreate,
  locale,
  t,
}: ProjectsDialogProps) {
  const [projects, setProjects] = useState<readonly ProjectSummary[]>([]);
  const [query, setQuery] = useState('');
  const [pendingDelete, setPendingDelete] = useState<ProjectSummary | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setProjects(await store.list());
    } finally {
      setLoading(false);
    }
  }, [store]);

  useEffect(() => {
    if (open) void refresh();
  }, [open, refresh]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return projects;
    return projects.filter(
      (project) =>
        project.name.toLowerCase().includes(needle) ||
        project.preview.toLowerCase().includes(needle),
    );
  }, [projects, query]);

  const formatDate = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale === 'pt' ? 'pt-BR' : 'en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    return (iso: string): string => {
      const date = new Date(iso);
      return Number.isNaN(date.getTime()) ? '—' : formatter.format(date);
    };
  }, [locale]);

  const handleOpen = async (id: string): Promise<void> => {
    const record = await store.get(id);
    if (record) {
      onOpen(record);
      onClose();
    }
  };

  const handleRename = async (project: ProjectSummary): Promise<void> => {
    const next = globalThis.prompt?.(t('projects.rename'), project.name);
    if (next === null || next === undefined) return;
    await store.rename(project.id, next.trim() || project.name);
    await refresh();
  };

  return (
    <>
      <Dialog
        open={open && pendingDelete === null}
        onClose={onClose}
        title={t('projects.title')}
        description={t('projects.count', { count: projects.length })}
        size="lg"
        footer={
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              {t('common.close')}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                onCreate();
                onClose();
              }}
            >
              {t('projects.create')}
            </Button>
          </DialogFooter>
        }
      >
        <label className="mb-3 block">
          <span className="fs-sr-only">{t('projects.search')}</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('projects.search')}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-ink"
          />
        </label>

        {visible.length === 0 ? (
          <EmptyState
            icon={<FolderOpen />}
            title={
              loading
                ? t('common.loading')
                : query
                  ? t('projects.noMatches', { query })
                  : t('projects.empty')
            }
            body={query ? undefined : t('projects.emptyHint')}
          />
        ) : (
          <ul className="flex flex-col gap-1.5">
            {visible.map((project) => (
              <li
                key={project.id}
                className={[
                  'flex items-center gap-2 rounded-panel border p-2.5',
                  project.id === currentProjectId
                    ? 'border-accent bg-accent-soft/40'
                    : 'border-border bg-surface',
                ].join(' ')}
              >
                <button
                  type="button"
                  onClick={() => void handleOpen(project.id)}
                  className="min-w-0 flex-1 text-left"
                >
                  <span className="block truncate text-sm font-medium text-ink">
                    {project.name || t('projects.untitled')}
                  </span>
                  <span className="block truncate text-xs text-ink-faint">
                    {t('projects.lastEdited', { date: formatDate(project.updatedAt) })}
                  </span>
                </button>

                <IconButton
                  label={t('projects.rename')}
                  variant="ghost"
                  compact
                  icon={<Edit3 />}
                  onClick={() => void handleRename(project)}
                />
                <IconButton
                  label={t('projects.duplicate')}
                  variant="ghost"
                  compact
                  icon={<Copy />}
                  onClick={() => void store.duplicate(project.id).then(refresh)}
                />
                <IconButton
                  label={t('projects.delete')}
                  variant="ghost"
                  compact
                  icon={<Trash />}
                  onClick={() => setPendingDelete(project)}
                />
              </li>
            ))}
          </ul>
        )}
      </Dialog>

      <Dialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title={t('projects.delete')}
        description={
          pendingDelete
            ? t('projects.deleteConfirm', { name: pendingDelete.name || t('projects.untitled') })
            : ''
        }
        size="sm"
        footer={
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                const target = pendingDelete;
                setPendingDelete(null);
                if (target) void store.remove(target.id).then(refresh);
              }}
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        }
      />
    </>
  );
}
